import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button, Notice } from '../design/index.js'

interface Props { children: ReactNode; onReset?: () => void }
interface State { error: Error | null }

/**
 * Keeps one broken page from taking the whole application with it.
 *
 * Without this, a single component throwing unmounted the entire React tree —
 * masthead included — and the only way back was a hard reload. An operator
 * seeing a blank screen has no way of knowing that.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept for the browser console; a real deployment ships this to the
    // error tracker along with the request id.
    console.error('Page failed to render', error, info.componentStack)
  }

  private reset = (): void => {
    this.setState({ error: null })
    this.props.onReset?.()
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children
    return (
      <div style={{ padding: '32px 0', maxWidth: 720 }}>
        <Notice kind="bad">
          <strong style={{ fontWeight: 700 }}>This page could not be shown.</strong>
          <div style={{ marginTop: 8 }}>
            Nothing you were doing has been lost — the rest of the application is still working, and
            you can go back and carry on.
          </div>
          <div style={{ marginTop: 12, font: "400 13.5px/1.5 var(--font-mono)", color: 'var(--muted)' }}>
            {error.message}
          </div>
        </Notice>
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <Button kind="primary" onClick={this.reset}>Try again</Button>
          <Button kind="ghost" onClick={() => { window.history.back() }}>Go back</Button>
        </div>
      </div>
    )
  }
}
